import React from 'react';
import { formatEtat, getEtatColor } from '../utils/etatFormatter';
import LocationDisplaySimple from './LocationDisplaySimple';

interface ObjetCardMobileProps {
  objet: any;
  viewMode: 'liste' | 'card';
  onEdit: (objet: any) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canArchive?: boolean;
  canMove?: boolean;
}

const ObjetCardMobile: React.FC<ObjetCardMobileProps> = ({ 
  objet, 
  viewMode, 
  onEdit, 
  canEdit = true, 
  canDelete = true,
  canArchive = true,
  canMove = true
}) => {
  const renderEtatBadge = () => {
    const etatFormatted = formatEtat(objet.etat);
    const colorClasses = getEtatColor(objet.etat);
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorClasses}`}>
        {etatFormatted}
      </span>
    );
  };

  const getLocationData = () => {
    if (objet.box) {
      return {
        site: objet.box.zone?.piece?.batiment?.site || objet.box.piece?.batiment?.site,
        batiment: objet.box.zone?.piece?.batiment || objet.box.piece?.batiment,
        piece: objet.box.zone?.piece || objet.box.piece,
        zone: objet.box.zone,
        box: objet.box
      };
    } else if (objet.zone) {
      return {
        site: objet.zone.piece?.batiment?.site,
        batiment: objet.zone.piece?.batiment,
        piece: objet.zone.piece,
        zone: objet.zone,
        box: null
      };
    }
    return null;
  };

  if (viewMode === 'liste') {
    return (
      <div className="flex items-start gap-3">
        {/* Photo miniature */}
        <div className="flex-shrink-0 w-14 h-14 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
          {objet.photos && objet.photos.length > 0 ? (
            <img 
              src={objet.photos[0].url} 
              alt={objet.nom} 
              className="w-full h-full object-cover" 
            />
          ) : (
            <span className="text-gray-400 text-2xl">📦</span>
          )}
        </div>

        {/* Contenu principal - Layout vertical optimisé */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Ligne 1: Nom + État (horizontal) */}
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0">
              {objet.nom}
            </h3>
            <div className="flex-shrink-0">
              {renderEtatBadge()}
            </div>
          </div>

          {/* Ligne 2: Catégorie + Quantité */}
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-xs text-gray-600 truncate flex-1 min-w-0">
              {objet.categorie}{objet.sousCategorie && ` · ${objet.sousCategorie}`}
            </p>
            <span className="text-xs text-gray-500 flex-shrink-0 font-medium">×{objet.quantite}</span>
          </div>

          {/* Ligne 3: Localisation (version verticale pour mobile) */}
          <div className="mb-1.5">
            <div className="flex flex-col gap-0.5">
              {getLocationData()?.site && (
                <span className="text-xs text-gray-600">
                  📍 {getLocationData()?.site?.nom}
                </span>
              )}
              {getLocationData()?.piece && (
                <span className="text-xs text-gray-600">
                  🚪 {getLocationData()?.piece?.nom}
                </span>
              )}
              {(getLocationData()?.zone || getLocationData()?.box) && (
                <span className="text-xs text-gray-600">
                  {getLocationData()?.box ? '📦' : '🏠'} {getLocationData()?.box?.nom || getLocationData()?.zone?.nom}
                </span>
              )}
            </div>
          </div>

          {/* Ligne 4: Badges (Besoin achat + SKU) */}
          <div className="flex items-center gap-2 flex-wrap">
            {objet.besoinAchat && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                🛒
              </span>
            )}
            {objet.sku && (
              <span className="text-xs text-gray-400 font-mono truncate">{objet.sku}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mode card
  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${canEdit ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow ${
        objet.besoinAchat ? 'border-l-4 border-l-orange-500 bg-orange-50' : ''
      }`}
      onClick={canEdit ? () => onEdit(objet) : undefined}
    >
      {/* Photo */}
      <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
        {objet.photos && objet.photos.length > 0 ? (
          <img 
            src={objet.photos[0].url} 
            alt={objet.nom} 
            className="w-full h-full object-cover" 
          />
        ) : (
          <span className="text-gray-400 text-2xl">📷</span>
        )}
      </div>

      {/* Contenu */}
      <div className="p-3">
        {/* Ligne 1: Nom + État */}
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0">
            {objet.nom}
          </h3>
          <div className="flex-shrink-0">
            {renderEtatBadge()}
          </div>
        </div>

        {/* Ligne 2: Catégorie + Quantité */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs text-gray-600 truncate flex-1 min-w-0">
            {objet.categorie}{objet.sousCategorie && ` · ${objet.sousCategorie}`}
          </p>
          <span className="text-xs text-gray-500 flex-shrink-0 font-medium">×{objet.quantite}</span>
        </div>

        {/* Ligne 3: Localisation (icônes verticales) */}
        <div className="mb-2 space-y-1">
          {getLocationData()?.site && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">📍</span>
              <span className="truncate">{getLocationData()?.site?.nom}</span>
            </div>
          )}
          {getLocationData()?.piece && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">🚪</span>
              <span className="truncate">{getLocationData()?.piece?.nom}</span>
            </div>
          )}
          {(getLocationData()?.zone || getLocationData()?.box) && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">{getLocationData()?.box ? '📦' : '🏠'}</span>
              <span className="truncate">{getLocationData()?.box?.nom || getLocationData()?.zone?.nom}</span>
            </div>
          )}
        </div>

        {/* Ligne 4: Badges compacts */}
        <div className="flex items-center gap-2 flex-wrap">
          {objet.besoinAchat && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
              🛒
            </span>
          )}
          {objet.sku && (
            <span className="text-xs text-gray-400 font-mono truncate max-w-full">{objet.sku}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjetCardMobile;
